(function () {
  'use strict';

  var SELECTOR = '[data-about-wordmark-hero]';
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function clamp(value) {
    return Math.max(0, Math.min(1, value));
  }

  function smoothstep(start, end, value) {
    var progress = clamp((value - start) / (end - start));
    return progress * progress * (3 - 2 * progress);
  }

  function pixels(value) {
    return value.toFixed(2) + 'px';
  }

  function initialize(section) {
    if (section.dataset.aboutWordmarkInitialized === 'true') return;
    section.dataset.aboutWordmarkInitialized = 'true';

    var nextSection = section.nextElementSibling;
    var hasStartHandoff = nextSection && nextSection.matches('[data-about-start-story]');
    var handoffMeasure = section.querySelector('[data-about-handoff-measure]');
    var sentence = section.querySelector('.about-wordmark-hero__sentence');
    var poolClause = section.querySelector('.about-wordmark-hero__pool-clause');
    var pool = section.querySelector('.about-wordmark-hero__word--pool');
    var ease = section.querySelector('.about-wordmark-hero__word--ease');
    if (!sentence || !poolClause || !pool || !ease || !('IntersectionObserver' in window)) return;

    var frame = null;
    var visible = false;
    var measurement = {
      poolX: 0,
      poolY: 0,
      easeX: 0,
      easeY: 0,
      lift: 0
    };

    function measure() {
      var fontSize = parseFloat(window.getComputedStyle(sentence).fontSize) || 64;
      var join = fontSize * -0.055;
      var groupWidth = pool.offsetWidth + ease.offsetWidth + join;
      var targetLeft = (sentence.clientWidth - groupWidth) / 2;
      var targetTop = (sentence.clientHeight - Math.max(poolClause.offsetHeight, ease.offsetHeight)) / 2;

      measurement.poolX = targetLeft - poolClause.offsetLeft;
      measurement.poolY = targetTop - poolClause.offsetTop;
      measurement.easeX = targetLeft + pool.offsetWidth + join - ease.offsetLeft;
      measurement.easeY = targetTop - ease.offsetTop;
      measurement.lift = window.innerHeight * (window.innerWidth <= 700 ? 0.2 : 0.21);
    }

    function setProgress(progress) {
      var expand = smoothstep(0.08, 0.43, progress);
      var copyReveal = smoothstep(0.18, 0.48, progress);
      var sentenceLift = smoothstep(0.45, 0.7, progress);
      var ruleReveal = smoothstep(0.52, 0.82, progress);
      var cleanReveal = smoothstep(0.53, 0.68, progress);
      var timeReveal = smoothstep(0.6, 0.75, progress);
      var earthReveal = smoothstep(0.67, 0.82, progress);

      section.style.setProperty('--about-pool-x', pixels(measurement.poolX * (1 - expand)));
      section.style.setProperty('--about-pool-y', pixels(measurement.poolY * (1 - expand)));
      section.style.setProperty('--about-ease-x', pixels(measurement.easeX * (1 - expand)));
      section.style.setProperty('--about-ease-y', pixels(measurement.easeY * (1 - expand)));
      section.style.setProperty('--about-copy-reveal', copyReveal.toFixed(4));
      section.style.setProperty('--about-copy-y', pixels((1 - copyReveal) * 18));
      section.style.setProperty('--about-sentence-y', pixels(measurement.lift * -sentenceLift));
      section.style.setProperty('--about-rule-scale', ruleReveal.toFixed(4));
      section.style.setProperty('--about-meaning-clean', cleanReveal.toFixed(4));
      section.style.setProperty('--about-meaning-clean-y', pixels((1 - cleanReveal) * 24));
      section.style.setProperty('--about-meaning-time', timeReveal.toFixed(4));
      section.style.setProperty('--about-meaning-time-y', pixels((1 - timeReveal) * 24));
      section.style.setProperty('--about-meaning-earth', earthReveal.toFixed(4));
      section.style.setProperty('--about-meaning-earth-y', pixels((1 - earthReveal) * 24));
    }

    function currentProgress() {
      var rect = section.getBoundingClientRect();
      var handoffHold = hasStartHandoff && handoffMeasure && window.innerWidth > 700
        ? handoffMeasure.getBoundingClientRect().height
        : 0;
      var travel = Math.max(1, rect.height - window.innerHeight - handoffHold);
      return clamp(-rect.top / travel);
    }

    function render() {
      frame = null;
      if (!visible || reducedMotion.matches) return;
      setProgress(currentProgress());
    }

    function requestRender() {
      if (frame === null && visible && !reducedMotion.matches) {
        frame = window.requestAnimationFrame(render);
      }
    }

    function stopRendering() {
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = null;
    }

    function handleMotionChange() {
      if (reducedMotion.matches) {
        stopRendering();
        section.classList.remove('is-ready');
      } else {
        measure();
        section.classList.add('is-ready');
        setProgress(currentProgress());
        requestRender();
      }
    }

    var observer = new IntersectionObserver(function (entries) {
      visible = entries[0].isIntersecting;
      if (visible) {
        measure();
        requestRender();
      } else {
        stopRendering();
      }
    }, { rootMargin: '10% 0px' });

    var resizeObserver = null;
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(function () {
        measure();
        if (!reducedMotion.matches) setProgress(currentProgress());
      });
      resizeObserver.observe(sentence);
    }

    observer.observe(section);
    window.addEventListener('scroll', requestRender, { passive: true });
    handleMotionChange();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        measure();
        if (!reducedMotion.matches) setProgress(currentProgress());
      });
    }

    if (reducedMotion.addEventListener) {
      reducedMotion.addEventListener('change', handleMotionChange);
    } else {
      reducedMotion.addListener(handleMotionChange);
    }

    section._aboutWordmarkCleanup = function () {
      stopRendering();
      observer.disconnect();
      window.removeEventListener('scroll', requestRender);
      if (resizeObserver) resizeObserver.disconnect();
      if (reducedMotion.removeEventListener) {
        reducedMotion.removeEventListener('change', handleMotionChange);
      } else {
        reducedMotion.removeListener(handleMotionChange);
      }
      delete section._aboutWordmarkCleanup;
    };
  }

  function initializeAll(root) {
    var scope = root || document;
    scope.querySelectorAll(SELECTOR).forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initializeAll(); });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initializeAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var section = event.target.querySelector(SELECTOR);
    if (section && section._aboutWordmarkCleanup) section._aboutWordmarkCleanup();
  });
})();
